/**
 * CategoryBoard.tsx
 *
 * Categories and the records filed under them, on one page — the admin layout
 * for Products, Appointment Types, Documents, the message templates and Lab
 * Decks. Categories are listed in the order the page gives them (the pages
 * sort them A–Z) and are never dragged.
 *
 * Drag and drop files a record under another category (2026-10-07, user: the
 * boards assign records to categories, they no longer order them — Prospera's
 * pickers are alphabetical and searchable):
 *
 *   - a category's records are listed A–Z (`getRecordTitle`);
 *   - open a category, grab a record by its grip, and drop it on any other
 *     category — its header or its open list. Dropping it back where it
 *     started does nothing.
 *
 * The moved record goes last in its new category's saved order, so the
 * records already there keep theirs (`onReorderRecords` gets that category's
 * records in the order the page gave them, plus the moved one at the end).
 *
 * `ordered` keeps the older behaviour for a board whose order is content (Lab
 * Decks: the viewer walks a patient through a deck's slides in order):
 * records keep the given order and drag to reorder inside a category too,
 * where the drop spot is their place.
 *
 * Each drop saves at once. The board moves first and calls the page's save;
 * if the save fails it snaps back to where things were and reports the error.
 * The page owns the data and the API calls. This component owns only the
 * drag mechanics and layout. The rules live in categoryBoardState.ts.
 *
 * Columns: the page may pass a `columnHeader` (a sticky header row) laid out
 * like its record rows. Every row keeps a fixed-width grip slot on the left
 * and a fixed-width actions slot on the right, so the header lines up with
 * the rows whether or not grips, badges or actions are shown. The header is
 * styled like an Admin list's table header (AdminList's AdminListTable, after
 * the original Users list: slate, text-xs semibold, titles as written — not
 * uppercase) and titles the actions slot "Actions", right-aligned, as Lead
 * Sources does. Record rows are text-sm, as table rows. A board never sorts
 * from its titles: its titles are not sort buttons.
 *
 * Records with no category sit in a trailing "Uncategorized" bucket that can
 * be dragged out of but not into (see categoryBoardState). It is shown only
 * when it holds something.
 *
 * On a Locked Admin page (useAdminPageLevel) the board is read-only: drag is
 * off (no grips, and a drop saves nothing), and the page's category and
 * record icons — AdminRowActions — render nothing. Categories still open and
 * close, and nothing says the board is locked (R18).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Collision,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, ChevronRight, GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';
import { ADMIN_CHILD_ROW_CLASS, ADMIN_PARENT_ROW_CLASS } from './AdminList';
import { useAdminPageLevel } from './AdminPageLevelContext';
import {
  UNCATEGORIZED,
  buildBoardState,
  compareTitles,
  containerOf,
  moveRecord,
  recordSave,
  type BoardState,
  type ContainerKey,
} from './categoryBoardState';

export interface BoardCategory {
  id: number;
  title: string;
  /** Soft-deleted (shown only while the page lists deleted records). */
  deleted?: boolean;
}

export interface CategoryBoardProps<R> {
  /** In display order (the pages sort them A–Z). */
  categories: BoardCategory[];
  /** Every record, each category's records in their saved order. */
  records: R[];
  getRecordId: (record: R) => number;
  /** The record's category, or null / 0 for none. */
  getRecordCategoryId: (record: R) => number | null;
  /** Lists each category's records A–Z by this title (ignoring case). Ignored when `ordered`. */
  getRecordTitle?: (record: R) => string;
  renderRecord: (record: R) => React.ReactNode;
  /**
   * A sticky header row above the board, laid out like renderRecord's row
   * (the board adds the grip and actions spacers around it).
   */
  columnHeader?: React.ReactNode;
  /** Summary cells on a category's header row, between its title and its actions. */
  renderCategoryColumns?: (category: BoardCategory) => React.ReactNode;
  /** Icon buttons on the right of a category's header row (add, edit, delete / restore). */
  renderCategoryActions?: (category: BoardCategory) => React.ReactNode;
  /** Icon buttons on the right of a record row (edit, delete / restore). */
  renderRecordActions?: (record: R) => React.ReactNode;
  /** True for a soft-deleted record: shown muted with a Deleted badge, never draggable. */
  isRecordDeleted?: (record: R) => boolean;
  /**
   * Saves a drop: the category the record now belongs to and that category's
   * records — in their saved order with the moved one last, or (`ordered`)
   * in their new order.
   */
  onReorderRecords: (categoryId: number, recordIds: number[]) => Promise<unknown>;
  /** Records keep the given order and drag to reorder inside a category too (Lab Decks' slides). */
  ordered?: boolean;
  onSaveError?: (error: unknown) => void;
  /** Singular and plural, e.g. ['product', 'products']. */
  recordNoun?: [string, string];
  uncategorizedLabel?: string;
  /** Hide the grips — for a filtered view, where a partial list must not be saved. Always on at Locked. */
  dragDisabled?: boolean;
}

const recDragId = (id: number) => `rec:${id}`;
const boxDropId = (key: ContainerKey) => `box:${key}`;
/** Move-only boards: a whole category section is the drop target. */
const sectionDropId = (key: ContainerKey) => `drop:${key}`;

const parseId = (dragId: string | number) => Number(String(dragId).split(':')[1]);

/**
 * The right-hand actions slot: a fixed width (four icon buttons) so columns
 * line up from row to row, and a clear gap from the last data column.
 */
export const ACTIONS_SLOT = 'ml-6 flex w-28 shrink-0 items-center justify-end gap-1';
/** The left-hand grip slot (the grip, or an empty spacer of the same width). */
export const GRIP_SLOT = 'flex w-4 shrink-0 items-center justify-center';
/** A drop target under a dragged record. */
export const DROP_TARGET_CLASS = 'ring-2 ring-indigo-300 bg-indigo-50/60';

/**
 * The line a board page shows under its toolbar while dragging is off, or
 * null while it is on. A searched, filtered or deleted-rows view holds a
 * partial list, which is never saved.
 */
export function boardDragHint({
  searching,
  filtering,
  showDeleted,
}: {
  searching: boolean;
  filtering: boolean;
  showDeleted: boolean;
}): string | null {
  if (searching && filtering) return 'Clear the search and filters to drag and drop.';
  if (searching) return 'Clear the search to drag and drop.';
  if (filtering) return 'Clear the filters to drag and drop.';
  if (showDeleted) return 'Turn off Show deleted to drag and drop.';
  return null;
}

/**
 * Applies a change at once, then saves it. If the save fails the change is
 * undone and the error reported — the board never shows a state the server
 * did not accept.
 */
export async function applyThenSave(
  apply: () => void,
  save: () => Promise<unknown>,
  rollback: () => void,
  onError?: (error: unknown) => void,
): Promise<boolean> {
  apply();
  try {
    await save();
    return true;
  } catch (error) {
    rollback();
    onError?.(error);
    return false;
  }
}

/**
 * Move-only boards: the drop target is the innermost accepted droppable under
 * the pointer (a record row inside an open category beats the category). A
 * keyboard drag has no pointer, so the dragged row's centre stands in.
 */
export function innermostUnderPointer(accept: (droppableId: string) => boolean): CollisionDetection {
  return ({ droppableContainers, droppableRects, pointerCoordinates, collisionRect }) => {
    const point = pointerCoordinates ?? {
      x: collisionRect.left + collisionRect.width / 2,
      y: collisionRect.top + collisionRect.height / 2,
    };
    const hits: (Collision & { area: number })[] = [];
    for (const container of droppableContainers) {
      if (!accept(String(container.id))) continue;
      const rect = droppableRects.get(container.id);
      if (!rect) continue;
      if (point.x < rect.left || point.x > rect.right || point.y < rect.top || point.y > rect.bottom) continue;
      hits.push({ id: container.id, data: { droppableContainer: container }, area: rect.width * rect.height });
    }
    return hits.sort((a, b) => a.area - b.area).map(({ id, data }) => ({ id, data }));
  };
}

export function CategoryBoard<R>({
  categories,
  records,
  getRecordId,
  getRecordCategoryId,
  getRecordTitle,
  renderRecord,
  columnHeader,
  renderCategoryColumns,
  renderCategoryActions,
  renderRecordActions,
  isRecordDeleted,
  onReorderRecords,
  ordered = false,
  onSaveError,
  recordNoun = ['item', 'items'],
  uncategorizedLabel = 'Uncategorized',
  dragDisabled: dragDisabledProp = false,
}: CategoryBoardProps<R>) {
  // A Locked page never drags.
  const locked = useAdminPageLevel() === 'locked';
  const dragDisabled = dragDisabledProp || locked;
  const recordById = useMemo(() => {
    const map = new Map<number, R>();
    for (const r of records) map.set(getRecordId(r), r);
    return map;
  }, [records, getRecordId]);

  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  // Per-row pieces handed to every RecordList, keyed by record id.
  const rowContent = (id: number) => {
    const record = recordById.get(id);
    return record === undefined ? null : renderRecord(record);
  };
  const rowActions = (id: number) => {
    const record = recordById.get(id);
    return record === undefined || !renderRecordActions ? null : renderRecordActions(record);
  };
  const rowDeleted = (id: number) => {
    const record = recordById.get(id);
    return record !== undefined && !!isRecordDeleted?.(record);
  };

  // The board keeps each category's records in their saved order (what a move
  // saves); a move-only board shows them A–Z.
  const shown = (ids: number[]): number[] => {
    if (ordered || !getRecordTitle) return ids;
    const title = (id: number) => {
      const record = recordById.get(id);
      return record === undefined ? '' : getRecordTitle(record);
    };
    return [...ids].sort((a, b) => compareTitles(title(a), title(b)) || a - b);
  };

  const fromProps = useMemo((): BoardState => {
    const known = new Set(categories.map((c) => c.id));
    const byCategory: Record<number, number[]> = {};
    const loose: number[] = [];
    for (const r of records) {
      const cat = getRecordCategoryId(r);
      if (cat && known.has(cat)) (byCategory[cat] ??= []).push(getRecordId(r));
      else loose.push(getRecordId(r));
    }
    return buildBoardState(
      categories.map((c) => c.id),
      byCategory,
      loose,
    );
  }, [categories, records, getRecordCategoryId, getRecordId]);

  const [board, setBoard] = useState<BoardState>(fromProps);
  // Categories start collapsed, as the pages always did: records mount only
  // for the categories someone opens, which keeps large catalogs fast. A
  // collapsed category still takes a drop on its header.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [looseOpen, setLooseOpen] = useState(false);
  const [activeId, setActiveId] = useState<number | null>(null);
  const atDragStart = useRef<BoardState>(fromProps);

  // New data from the page replaces whatever the board was showing.
  useEffect(() => {
    setBoard(fromProps);
  }, [fromProps]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Ordered: a record lands among records and category boxes — never in the
  // Uncategorized bucket. Move-only: on a category section, the innermost
  // under the pointer.
  const orderedCollisions: CollisionDetection = (args) =>
    closestCorners({
      ...args,
      droppableContainers: args.droppableContainers.filter((c) => c.data.current?.container !== UNCATEGORIZED),
    });
  const moveCollisions = useMemo(() => innermostUnderPointer((id) => id.startsWith('drop:')), []);

  function containerOfDropTarget(overId: string, state: BoardState): ContainerKey | null {
    if (overId.startsWith('box:')) return overId.slice(4);
    if (overId.startsWith('head:')) return overId.slice(5);
    if (overId.startsWith('drop:')) return overId.slice(5);
    if (overId.startsWith('rec:')) return containerOf(state, parseId(overId));
    return null;
  }

  function handleDragStart(event: DragStartEvent) {
    atDragStart.current = board;
    setActiveId(parseId(event.active.id));
  }

  // Ordered boards: crossing into another category moves the record there
  // while dragging, so the target list opens a gap at the pointer.
  function handleDragOver(event: DragOverEvent) {
    if (dragDisabled || !ordered || !event.over) return;
    const activeKey = String(event.active.id);
    // A collapsed category's header: moving the record there now would unmount
    // the row being dragged. The move is applied on drop instead.
    if (String(event.over.id).startsWith('head:')) return;

    setBoard((current) => {
      const recordId = parseId(activeKey);
      const from = containerOf(current, recordId);
      const to = containerOfDropTarget(String(event.over!.id), current);
      if (!from || !to || from === to || to === UNCATEGORIZED) return current;

      const overId = String(event.over!.id);
      const index = overId.startsWith('rec:')
        ? current.records[to].indexOf(parseId(overId))
        : current.records[to].length;
      return moveRecord(current, recordId, to, index);
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    // Drag off (a filtered view, or a Locked page): a drop moves and saves nothing.
    if (dragDisabled) return;
    const before = atDragStart.current;
    const overId = event.over ? String(event.over.id) : null;
    if (!overId) {
      setBoard(before);
      return;
    }

    const recordId = parseId(event.active.id);
    const to = containerOfDropTarget(overId, board);
    let after = board;
    if (!ordered) {
      // Filed last in the new category's saved order; the same category: no-op.
      if (to && to !== UNCATEGORIZED && to !== containerOf(board, recordId)) {
        after = moveRecord(board, recordId, to, board.records[to].length);
      }
    } else if (to && to !== UNCATEGORIZED && overId.startsWith('rec:')) {
      after = moveRecord(board, recordId, to, board.records[to].indexOf(parseId(overId)));
    } else if (to && to !== UNCATEGORIZED && overId.startsWith('head:')) {
      // Dropped on a collapsed category: file it at the end of that category.
      after = moveRecord(board, recordId, to, board.records[to].length);
    }

    const save = recordSave(before, after, recordId);
    if (!save) {
      setBoard(before);
      return;
    }
    void applyThenSave(
      () => setBoard(after),
      () => onReorderRecords(save.categoryId, save.ids),
      () => setBoard(before),
      onSaveError,
    );
  }

  function toggle(categoryId: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  }

  const noun = (n: number) => `${n} ${n === 1 ? recordNoun[0] : recordNoun[1]}`;
  const loose = board.records[UNCATEGORIZED] ?? [];
  const activeContainer = activeId === null ? null : containerOf(board, activeId);
  const activeRecord = activeId === null ? undefined : recordById.get(activeId);

  const listProps = {
    ordered,
    renderRecord: rowContent,
    renderActions: rowActions,
    isDeleted: rowDeleted,
    dragDisabled,
    activeId,
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={ordered ? orderedCollisions : moveCollisions}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveId(null);
        setBoard(atDragStart.current);
      }}
    >
      <div className="space-y-2" data-testid="category-board">
        {columnHeader && (
          <div
            data-testid="category-board-columns"
            className="sticky top-0 z-20 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs font-semibold whitespace-nowrap text-slate-600"
          >
            <span className={GRIP_SLOT} aria-hidden="true" />
            <div className="min-w-0 flex-1">{columnHeader}</div>
            {/* Right-aligned over the row actions, as Lead Sources' Actions column. */}
            <span className={ACTIONS_SLOT}>{locked ? '' : 'Actions'}</span>
          </div>
        )}

        {board.categoryIds.map((categoryId) => {
          const recordIds = board.records[String(categoryId)] ?? [];
          const category = categoryById.get(categoryId) ?? { id: categoryId, title: '' };
          return (
            <CategorySection
              key={categoryId}
              category={category}
              countLabel={noun(recordIds.length)}
              // An empty category has nothing to open: no chevron, no toggle.
              hasRecords={recordIds.length > 0}
              expanded={expanded.has(categoryId) && recordIds.length > 0}
              onToggle={() => toggle(categoryId)}
              columns={renderCategoryColumns?.(category)}
              actions={renderCategoryActions?.(category)}
              ordered={ordered}
              dropDisabled={dragDisabled || !!category.deleted}
              isSource={activeContainer === String(categoryId)}
            >
              <RecordList
                containerKey={String(categoryId)}
                recordIds={shown(recordIds)}
                // Never shown: an empty category cannot be opened (records drop on its header).
                emptyLabel=""
                {...listProps}
              />
            </CategorySection>
          );
        })}

        {loose.length > 0 && (
          <section className="rounded-lg border border-slate-300 bg-white" aria-label={uncategorizedLabel}>
            <header
              className={cn('flex items-center gap-2 rounded-lg px-3 py-2.5', ADMIN_PARENT_ROW_CLASS, looseOpen && 'rounded-b-none')}
              onClick={() => setLooseOpen((open) => !open)}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLooseOpen((open) => !open);
                }}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                aria-expanded={looseOpen}
              >
                {looseOpen ? (
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                ) : (
                  <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
                )}
                <span className="font-medium text-slate-600">{uncategorizedLabel}</span>
                <Badge variant="neutral" size="sm">{noun(loose.length)}</Badge>
              </button>
              {!dragDisabled && looseOpen && (
                <span className="ml-6 text-xs text-slate-400">Drag onto a category to file it</span>
              )}
            </header>
            {looseOpen && <RecordList containerKey={UNCATEGORIZED} recordIds={shown(loose)} emptyLabel="" {...listProps} />}
          </section>
        )}
      </div>

      {/* Move-only boards: the dragged row floats under the pointer; the lists stay put. */}
      {!ordered && (
        <DragOverlay dropAnimation={null}>
          {activeRecord !== undefined && activeId !== null ? (
            <div className="flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm shadow-lg">
              <span className={GRIP_SLOT}>
                <GripVertical className="h-4 w-4 text-slate-400" />
              </span>
              <div className="min-w-0 flex-1">{rowContent(activeId)}</div>
            </div>
          ) : null}
        </DragOverlay>
      )}
    </DndContext>
  );
}

// ─── Pieces ─────────────────────────────────────────────────────────────────

interface CategorySectionProps {
  category: BoardCategory;
  countLabel: string;
  /** False: no chevron and no toggle, so an empty category reads as empty. */
  hasRecords: boolean;
  expanded: boolean;
  onToggle: () => void;
  columns?: React.ReactNode;
  actions?: React.ReactNode;
  ordered: boolean;
  /** No record may be dropped here (a filtered or read-only view, or a deleted category). */
  dropDisabled: boolean;
  /** The dragged record's own category: never highlighted as a target. */
  isSource: boolean;
  children: React.ReactNode;
}

function CategorySection({
  category,
  countLabel,
  hasRecords,
  expanded,
  onToggle,
  columns,
  actions,
  ordered,
  dropDisabled,
  isSource,
  children,
}: CategorySectionProps) {
  // Move-only: the whole section (header and open list) takes the drop.
  const { setNodeRef: setSectionDropRef, isOver: isOverSection } = useDroppable({
    id: sectionDropId(String(category.id)),
    data: { type: 'box', container: String(category.id) },
    disabled: ordered || dropDisabled,
  });
  // Ordered: while collapsed, the header is the drop target, so a record can be
  // filed into a category without opening it (it lands at the end).
  const { setNodeRef: setHeaderDropRef, isOver: isOverHeader } = useDroppable({
    id: `head:${category.id}`,
    data: { type: 'box', container: String(category.id) },
    disabled: !ordered || expanded || dropDisabled,
  });
  const target = !isSource && (isOverSection || isOverHeader);

  return (
    <section
      ref={setSectionDropRef}
      className={cn('rounded-lg border border-slate-200 bg-white', !ordered && target && DROP_TARGET_CLASS)}
      aria-label={category.title}
    >
      <header
        ref={setHeaderDropRef}
        // A click anywhere on the header expands or collapses it (Patient Notes' parent row).
        onClick={hasRecords ? onToggle : undefined}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-lg',
          hasRecords ? ADMIN_PARENT_ROW_CLASS : 'bg-slate-100 transition-colors',
          expanded && 'rounded-b-none',
          target && 'bg-indigo-50 hover:bg-indigo-50',
          ordered && isOverHeader && 'ring-1 ring-indigo-200',
        )}
      >
        {hasRecords ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
            )}
            <span className={cn('truncate font-medium', category.deleted ? 'text-slate-400 line-through' : 'text-slate-900')}>
              {category.title}
            </span>
            <Badge variant="neutral" size="sm">{countLabel}</Badge>
            {category.deleted && <Badge variant="error" size="sm">Deleted</Badge>}
          </button>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2" data-testid="empty-category">
            <span className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className={cn('truncate font-medium', category.deleted ? 'text-slate-400 line-through' : 'text-slate-900')}>
              {category.title}
            </span>
            <Badge variant="neutral" size="sm">{countLabel}</Badge>
            {category.deleted && <Badge variant="error" size="sm">Deleted</Badge>}
          </div>
        )}
        {columns}
        {actions && (
          <div className={ACTIONS_SLOT} onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </header>
      {expanded && children}
    </section>
  );
}

interface RecordListProps {
  containerKey: ContainerKey;
  recordIds: number[];
  ordered: boolean;
  renderRecord: (id: number) => React.ReactNode;
  renderActions: (id: number) => React.ReactNode;
  isDeleted: (id: number) => boolean;
  dragDisabled: boolean;
  activeId: number | null;
  emptyLabel: string;
}

function RecordList({ containerKey, recordIds, ordered, renderRecord, renderActions, isDeleted, dragDisabled, activeId, emptyLabel }: RecordListProps) {
  // Ordered boards only: the list takes a drop anywhere in it (a gap, its end).
  const { setNodeRef, isOver } = useDroppable({
    id: boxDropId(containerKey),
    data: { type: 'box', container: containerKey },
    disabled: !ordered || containerKey === UNCATEGORIZED,
  });

  const rows =
    recordIds.length === 0 && emptyLabel ? (
      <li className="px-4 py-3 text-sm text-slate-400">{emptyLabel}</li>
    ) : (
      recordIds.map((id) => {
        const rowProps = {
          recordId: id,
          containerKey,
          dragDisabled,
          deleted: isDeleted(id),
          actions: renderActions(id),
          children: renderRecord(id),
        };
        return ordered ? <SortableRecord key={id} {...rowProps} /> : <DraggableRecord key={id} {...rowProps} lifted={activeId === id} />;
      })
    );

  const list = (
    <ul ref={setNodeRef} className={cn('divide-y divide-slate-100 min-h-[2.5rem]', ordered && isOver && 'bg-indigo-50/50')}>
      {rows}
    </ul>
  );
  return ordered ? (
    <SortableContext items={recordIds.map(recDragId)} strategy={verticalListSortingStrategy}>
      {list}
    </SortableContext>
  ) : (
    list
  );
}

interface RecordRowProps {
  recordId: number;
  containerKey: ContainerKey;
  dragDisabled: boolean;
  deleted: boolean;
  actions: React.ReactNode;
  children: React.ReactNode;
}

/** Ordered boards: a row that slides aside as others are dragged past it. */
function SortableRecord({ recordId, containerKey, dragDisabled, deleted, actions, children }: RecordRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: recDragId(recordId),
    data: { type: 'record', container: containerKey },
    disabled: dragDisabled || deleted,
  });

  return (
    <RecordRow
      recordId={recordId}
      rowRef={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && 'relative z-10 shadow-md opacity-90')}
      deleted={deleted}
      actions={actions}
      grip={
        !dragDisabled && !deleted ? (
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
            aria-label="Drag to move"
            title="Drag to reorder or move to another category"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : null
      }
    >
      {children}
    </RecordRow>
  );
}

/** Move-only boards: a row picked up by its grip; it stays put, faded, while its copy floats. */
function DraggableRecord({ recordId, containerKey, dragDisabled, deleted, actions, children, lifted }: RecordRowProps & { lifted: boolean }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef } = useDraggable({
    id: recDragId(recordId),
    data: { type: 'record', container: containerKey },
    disabled: dragDisabled || deleted,
  });

  return (
    <RecordRow
      recordId={recordId}
      rowRef={setNodeRef}
      className={cn(lifted && 'opacity-40')}
      deleted={deleted}
      actions={actions}
      grip={
        !dragDisabled && !deleted ? (
          <button
            type="button"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
            aria-label="Drag to move"
            title="Drag onto another category to move it there"
          >
            <GripVertical className="h-4 w-4" />
          </button>
        ) : null
      }
    >
      {children}
    </RecordRow>
  );
}

function RecordRow({
  recordId,
  rowRef,
  style,
  className,
  deleted,
  actions,
  grip,
  children,
}: {
  recordId: number;
  rowRef: (node: HTMLElement | null) => void;
  style?: React.CSSProperties;
  className?: string;
  deleted: boolean;
  actions: React.ReactNode;
  grip: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <li
      ref={rowRef}
      style={style}
      className={cn('flex items-center gap-2 px-3 text-sm', ADMIN_CHILD_ROW_CLASS, deleted && 'bg-slate-50 hover:bg-slate-50', className)}
      data-record-id={recordId}
      data-deleted={deleted || undefined}
    >
      <span className={GRIP_SLOT}>{grip}</span>
      <div className={cn('min-w-0 flex-1', deleted && 'opacity-60')}>{children}</div>
      <div className={cn(ACTIONS_SLOT, 'py-1')}>
        {deleted && <Badge variant="error" size="sm">Deleted</Badge>}
        {actions}
      </div>
    </li>
  );
}
